<template>
  <div v-if="!isApproved" class="section-card reschedule-card">
    <div class="reschedule-header">
      <div class="reschedule-title-wrap">
        <div class="reschedule-icon">
          <v-icon size="18">mdi-calendar-refresh</v-icon>
        </div>
        <div>
          <p class="reschedule-title">Reschedule Requested</p>
          <p class="reschedule-subtitle">The customer requested a new appointment time.</p>
        </div>
      </div>

      <span class="reschedule-badge">Pending</span>
    </div>

    <div class="reschedule-schedule">
      <div class="reschedule-row">
        <div class="reschedule-row-icon">
          <v-icon size="16">mdi-calendar</v-icon>
        </div>
        <div>
          <p class="reschedule-row-label">Preferred date</p>
          <p class="reschedule-row-value">
            {{ preferredDateLabel }}
          </p>
        </div>
      </div>

      <div class="reschedule-row">
        <div class="reschedule-row-icon">
          <v-icon size="16">mdi-clock-outline</v-icon>
        </div>
        <div>
          <p class="reschedule-row-label">Preferred time</p>
          <p class="reschedule-row-value">
            {{ preferredTimeLabel }}
          </p>
        </div>
      </div>
    </div>

    <div v-if="reason" class="reschedule-reason">
      <p class="reschedule-reason-label">Reason</p>
      <p class="reschedule-reason-text">{{ reason }}</p>
    </div>

    <div class="reschedule-actions">
      <AppButtonFlat
        block
        color="success"
        prepend-icon="mdi-check-circle-outline"
        @click="handleApproveClick"
      >
        Approve Request
      </AppButtonFlat>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";
import { useModalStore } from "@/shared/stores/modal";
import { formatDate, formatTime } from "@/shared/utils/utils";
import AppointmentRescheduleApproveModal from "@/modules/vendor/components/AppointmentRescheduleApproveModal.vue";

const props = defineProps<{
  appointment: {
    id: number | string;
    reschedule_request?: {
      preferred_date: string | null;
      preferred_time: string | null;
      reason: string | null;
    } | null;
  } | null;
}>();

const modal = useModalStore();
const isApproved = ref(false);
const reason = computed(() => props.appointment?.reschedule_request?.reason || "");
const preferredDateLabel = computed(() =>
  formatDate(props.appointment?.reschedule_request?.preferred_date || ""),
);
const preferredTimeLabel = computed(() =>
  formatTime(props.appointment?.reschedule_request?.preferred_time || ""),
);

function handleApproveClick() {
  modal.open(
    AppointmentRescheduleApproveModal,
    {
      appointment: props.appointment,
      onSuccess: () => {
        isApproved.value = true;
      },
    },
    { title: "Approve Reschedule" }
  );
}
</script>

<style scoped>
.section-card {
  background: #fff;
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-color);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.reschedule-card {
  gap: 12px;
  background: linear-gradient(180deg, #fff7ed 0%, #ffffff 72%);
  border-color: #fed7aa;
}

.reschedule-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.reschedule-title-wrap {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
}

.reschedule-icon {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: #ffedd5;
  color: #f97316;
  flex-shrink: 0;
}

.reschedule-title {
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  color: #9a3412;
}

.reschedule-subtitle {
  margin: 4px 0 0;
  font-size: 13px;
  color: #b45309;
  line-height: 1.4;
}

.reschedule-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  background: #ffedd5;
  color: #c2410c;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  flex-shrink: 0;
}

.reschedule-schedule {
  display: grid;
  gap: 10px;
}

.reschedule-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #fde68a;
}

.reschedule-row-icon {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: #fff7ed;
  color: #f97316;
  flex-shrink: 0;
}

.reschedule-row-label {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #b45309;
}

.reschedule-row-value {
  margin: 4px 0 0;
  font-size: 14px;
  font-weight: 700;
  color: var(--color-title);
}

.reschedule-reason {
  padding: 12px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #fed7aa;
}

.reschedule-reason-label {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #b45309;
}

.reschedule-reason-text {
  margin: 4px 0 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-title);
  line-height: 1.5;
}

.reschedule-actions {
  margin-top: 2px;
}
</style>
